import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Zap, Flame, ArrowRight, Check, Sparkles, Plus } from 'lucide-react';
import { UPSELLS } from '../data/upsells';
import { KITS } from '../data/kits';
import { PaymentMethods } from './PaymentMethods';
interface ExtraComplementUpsellPopupProps {
  open: boolean;
  selectedKitIds?: string[];
  selectedUpsellIds: string[];
  onAccept: (extraComplementId: string) => void;
  onDecline: () => void;
}
// Fixed price for taking 2 complementary kits together.
const TWO_COMPLEMENTS_PRICE = 19990;
// Fixed price when the customer ALSO already has a main kit:
// 1 main kit + 2 complements all together.
const KIT_PLUS_TWO_COMPLEMENTS_PRICE = 34980;
export function ExtraComplementUpsellPopup({
  open,
  selectedKitIds = [],
  selectedUpsellIds,
  onAccept,
  onDecline
}: ExtraComplementUpsellPopupProps) {
  const currentComplement = UPSELLS.find(u => u.id === selectedUpsellIds[0]) ?? UPSELLS[0];
  const remaining = UPSELLS.filter(u => !selectedUpsellIds.includes(u.id));
  const [chosenExtraId, setChosenExtraId] = useState(remaining[0]?.id ?? UPSELLS[0].id);
  // Keep the chosen extra valid if the available list changes.
  useEffect(() => {
    if (open && remaining.length > 0 && !remaining.some(u => u.id === chosenExtraId)) {
      setChosenExtraId(remaining[0].id);
    }
  }, [open, remaining, chosenExtraId]);
  const chosenExtra = UPSELLS.find(u => u.id === chosenExtraId) ?? remaining[0] ?? UPSELLS[0];
  // Did the customer already pick a main kit BEFORE this upsell?
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const hasMainKit = selectedKits.length > 0;
  const mainKit = selectedKits[0];
  // Complements-only deal vs. main-kit-included deal.
  const offerPrice = hasMainKit ? KIT_PLUS_TWO_COMPLEMENTS_PRICE : TWO_COMPLEMENTS_PRICE;
  const complementsNormal = currentComplement.price + chosenExtra.price;
  const normalTwo = hasMainKit ? selectedKits.reduce((sum, k) => sum + k.price, 0) + complementsNormal : complementsNormal;
  const savings = normalTwo - offerPrice;
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onDecline]);
  return <AnimatePresence data-id="element-408">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[85] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onDecline} role="dialog" aria-modal="true" aria-labelledby="extra-upsell-title" data-id="element-409">
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-[1.75rem] shadow-2xl w-full max-w-lg overflow-hidden my-4 max-h-[95vh] flex flex-col" data-id="element-410">
            <button onClick={onDecline} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ink-light hover:text-ink flex items-center justify-center shadow-md transition-colors" data-id="element-411">
              <X className="w-4 h-4" data-id="element-412" />
            </button>
            {/* Header banner */}
            <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white px-4 sm:px-5 pt-3 sm:pt-5 pb-4 sm:pb-5 text-center overflow-hidden" data-id="element-413">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(252,211,77,0.25),transparent_55%)]" data-id="element-414" />
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl" data-id="element-415" />
              <div className="absolute -bottom-12 -right-10 w-44 h-44 bg-black/20 rounded-full blur-2xl" data-id="element-416" />
              <motion.div initial={{
            scale: 0,
            rotate: -10
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 18,
            delay: 0.05
          }} className="relative inline-flex mb-1.5" data-id="element-417">
                <motion.div animate={{
              scale: [1, 1.08, 1]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} className="absolute inset-0 bg-yellow-300/40 rounded-full blur-md" data-id="element-418" />
                <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-yellow-400 text-red-800 flex items-center justify-center shadow-lg border-2 sm:border-[3px] border-white/20" data-id="element-419">
                  <Plus className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} data-id="element-420" />
                </div>
              </motion.div>
              <motion.div animate={{
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="relative inline-flex items-center gap-1 sm:gap-1.5 bg-white/15 backdrop-blur-sm text-yellow-200 px-2 sm:px-2.5 py-0.5 rounded-full font-extrabold text-[8px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.16em] mb-1.5 sm:mb-2 border border-yellow-300/40" data-id="element-421">
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-422" />
                Oferta exclusiva · Solo aparece una vez
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-423" />
              </motion.div>
              <h2 id="extra-upsell-title" className="relative font-sans font-extrabold text-[15px] sm:text-2xl leading-tight tracking-tight mb-1 sm:mb-1.5" data-id="element-424">
                ¡Sumá uno más!
              </h2>
              <p className="relative text-[11px] sm:text-sm text-white/95 max-w-sm mx-auto leading-snug" data-id="element-425">
                Agregá un segundo complemento y llevate{' '}
                <span className="font-bold text-yellow-300 underline decoration-yellow-300/60 underline-offset-2" data-id="element-426">
                  {hasMainKit ? `tu kit principal + los 2 complementos por solo ${offerPrice.toLocaleString('es-AR')}` : `los dos por solo ${offerPrice.toLocaleString('es-AR')}`}
                </span>
                .
              </p>
            </div>
            {/* Body */}
            <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-1 overflow-y-auto" data-id="element-427">
              <div className="flex justify-center mb-2.5" data-id="element-428">
                <motion.div animate={{
              opacity: [1, 0.55, 1],
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} className="bg-gradient-to-r from-combo-green to-combo-green/80 text-white px-3 py-1 rounded-full font-extrabold text-[10px] sm:text-[11px] shadow-glow border-2 border-white tracking-wide" data-id="element-429">
                  {hasMainKit ? '🎁 KIT PRINCIPAL + 2 COMPLEMENTOS' : '🎁 2 COMPLEMENTOS · 1 PRECIO ESPECIAL'}
                </motion.div>
              </div>
              {hasMainKit ? <p className="text-[12px] sm:text-sm text-ink leading-snug mb-3 text-center" data-id="element-430">
                  Ya estás llevando <strong data-id="element-431">{mainKit.name}</strong> y elegiste{' '}
                  <strong data-id="element-432">{currentComplement.name}</strong>. Sumale un segundo
                  complemento y llevate{' '}
                  <strong className="text-combo-green" data-id="element-433">
                    el kit principal + los 2 complementos por $
                    {offerPrice.toLocaleString('es-AR')}
                  </strong>{' '}
                  en vez de ${normalTwo.toLocaleString('es-AR')}.
                </p> : <p className="text-[12px] sm:text-sm text-ink leading-snug mb-3 text-center" data-id="element-434">
                  Ya elegiste <strong data-id="element-435">{currentComplement.name}</strong>. Sumale
                  un segundo a elección y llevate{' '}
                  <strong className="text-combo-green" data-id="element-436">
                    ambos por ${offerPrice.toLocaleString('es-AR')}
                  </strong>{' '}
                  en vez de ${normalTwo.toLocaleString('es-AR')}.
                </p>}
              {/* Extra complement chooser */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-437">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-438">
                  <Sparkles className="w-3 h-3" style={{
                color: '#C9A84C'
              }} data-id="element-439" />
                  Elegí tu segundo complemento:
                </div>
                <div className="grid grid-cols-2 gap-1.5" data-id="element-440">
                  {remaining.map(u => {
                const active = u.id === chosenExtraId;
                return <button key={u.id} type="button" onClick={() => setChosenExtraId(u.id)} className={`flex items-center gap-1.5 text-left px-2 py-1.5 rounded-lg border transition-all ${active ? 'border-combo-green bg-combo-green/5 ring-1 ring-combo-green' : 'border-ink/10 hover:border-ink/30'}`} data-id="element-441">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: u.color
                  }} data-id="element-442" />
                        <span className="text-[11px] font-medium text-ink leading-tight flex-1 min-w-0" data-id="element-443">
                          {u.name.replace('Kit ', '')}
                        </span>
                        {active && <Check className="w-3 h-3 text-combo-green flex-shrink-0" strokeWidth={3} data-id="element-444" />}
                      </button>;
              })}
                </div>
              </div>
              {/* What's included */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-445">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-1.5" data-id="element-446">
                  {hasMainKit ? '✅ Esto te llevás:' : '✅ Tus 2 complementos:'}
                </div>
                <ul className="space-y-1" data-id="element-447">
                  {hasMainKit && <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-448">
                      <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-449" />
                      <span data-id="element-450">
                        <strong data-id="element-451">{mainKit.name}</strong>{' '}
                        <span className="text-ink-light text-[10px]" data-id="element-452">
                          (kit principal · ya elegido)
                        </span>
                      </span>
                    </li>}
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-453">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-454" />
                    <span data-id="element-455">
                      <strong data-id="element-456">{currentComplement.name}</strong>{' '}
                      <span className="text-ink-light text-[10px]" data-id="element-457">
                        (ya elegido)
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-458">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-459" />
                    <span data-id="element-460">
                      <strong data-id="element-461">{chosenExtra.name}</strong>{' '}
                      <span className="text-ink-light text-[10px]" data-id="element-462">
                        (nuevo)
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
              {/* Pricing */}
              <div className="bg-gradient-to-br from-cream-dark to-cream-dark/80 rounded-xl p-3 border border-gold/30" data-id="element-463">
                <div className="flex items-center justify-between gap-3" data-id="element-464">
                  <div className="text-left min-w-0" data-id="element-465">
                    <div className="text-[9px] text-ink-light uppercase tracking-wider font-bold" data-id="element-466">
                      {hasMainKit ? 'Kit principal + 2 complementos' : 'Los 2 complementos'}
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5" data-id="element-467">
                      <motion.span animate={{
                    scale: [1, 1.04, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="text-xl sm:text-2xl font-extrabold text-combo-green tabular-nums" data-id="element-468">
                        ${offerPrice.toLocaleString('es-AR')}
                      </motion.span>
                      <span className="text-[11px] sm:text-xs text-ink-light line-through decoration-red-500 decoration-2 tabular-nums" data-id="element-469">
                        ${normalTwo.toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-470">
                    <div className="inline-flex items-center gap-1 bg-combo-green text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-471">
                      Ahorrás ${savings.toLocaleString('es-AR')}
                    </div>
                    <div className="text-[9px] text-ink-light mt-1 flex items-center justify-end gap-1" data-id="element-472">
                      <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-473" />
                      Solo en esta pantalla
                    </div>
                  </div>
                </div>
              </div>
              <PaymentMethods className="mt-3" data-id="element-474" />
            </div>
            {/* CTAs */}
            <div className="px-4 sm:px-6 py-3 sm:py-3.5 bg-white border-t border-ink/5 space-y-1.5" data-id="element-475">
              <motion.button onClick={() => onAccept(chosenExtraId)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0 0 0 0 rgba(27,67,50,0.55)', '0 0 0 14px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="w-full bg-combo-green text-white py-2.5 sm:py-3 rounded-full font-extrabold text-[12px] sm:text-sm hover:bg-combo-green/90 transition-colors flex items-center justify-center gap-1.5 shadow-glow" data-id="element-476">
                <span className="truncate" data-id="element-477">
                  {hasMainKit ? `¡SÍ! LLEVAR TODO POR ${offerPrice.toLocaleString('es-AR')}` : `¡SÍ! LLEVAR LOS 2 POR ${offerPrice.toLocaleString('es-AR')}`}
                </span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" data-id="element-478" />
              </motion.button>
              <button onClick={onDecline} className="w-full text-ink-light hover:text-ink text-[11px] sm:text-xs font-medium underline underline-offset-4 transition-colors py-1" data-id="element-479">
                No gracias, continuar así →
              </button>
              <div className="flex items-center justify-center gap-3 text-[9px] sm:text-[10px] text-ink-light font-medium" data-id="element-480">
                <span className="flex items-center gap-1" data-id="element-481">
                  <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-482" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-483">
                  <Zap className="w-2.5 h-2.5 text-gold" data-id="element-484" /> Acceso Inmediato
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}